/**
 * Desktop notifications for members (TRUS-668). Headless; mounted once in the
 * member portal layout.
 *
 * Members get no bell rows for messages, so before this the ONLY way a new
 * message reached a member's desktop was web push. Push cannot register where
 * the network blocks Google's push servers, and the service worker suppresses a
 * push while a WeaveIn window is focused. This polls
 * GET /api/member-portal/inbox/new-messages (a cursor over the member's
 * conversations) and shows a notification for each new staff message:
 *
 *  - every 20 s, unthrottled in a background tab (lib/backgroundTimer), at once
 *    when the window comes back; backs off on errors (up to 5 minutes);
 *  - the first poll of a page only takes the cursor (old messages are not news);
 *  - shown through the service worker with the push's tag (`message-<id>`), so
 *    a push for the same message collapses into it; a push that already showed
 *    it here is not shown again. Without a registration: the Web Notifications API;
 *  - not for the conversation the member has open in a focused window.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMemberToken, memberApiFetch } from '@/lib/memberAuth';
import { backgroundTimeout } from '@/lib/backgroundTimer';
import { listenForPushShown, messageTag, showCollapsingNotification } from '@/lib/browserNotifications';
import { isWindowInBackground } from '@/lib/desktopMessageNotifications';
import { desktopNotifications } from '@/services/desktopNotifications';

export const MEMBER_POLL_MS = 20_000;
export const MEMBER_MAX_BACKOFF_MS = 300_000;

export interface MemberNewMessage {
  id: number;
  channel_id: number;
  title: string;
  body: string;
  urgent: boolean;
  link: string;
}

export interface MemberNewMessages {
  cursor: number;
  messages: MemberNewMessage[];
}

export async function fetchMemberNewMessages(after: number | null): Promise<MemberNewMessages> {
  const q = after === null ? '' : `?after=${encodeURIComponent(String(after))}`;
  const res = await memberApiFetch(`/api/member-portal/inbox/new-messages${q}`);
  if (!res.ok) throw new Error(`new-messages ${res.status}`);
  return res.json();
}

/** The member is reading this conversation right now (focused window, that thread open). */
export function isReadingConversation(channelId: number): boolean {
  if (typeof window === 'undefined' || isWindowInBackground()) return false;
  try {
    const u = new URL(window.location.href);
    return u.pathname.startsWith('/member/inbox') && u.searchParams.get('thread') === `channel:${channelId}`;
  } catch {
    return false;
  }
}

export function MemberMessageNotifier() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const token = getMemberToken();

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let cancelTimer: (() => void) | null = null;
    let inFlight: Promise<void> | null = null;
    let cursor: number | null = null;
    let failures = 0;
    listenForPushShown();

    const show = async (m: MemberNewMessage) => {
      if (isReadingConversation(m.channel_id)) return;
      const result = await showCollapsingNotification({
        title: m.title,
        body: m.body,
        tag: messageTag(m.id),
        url: m.link,
        requireInteraction: m.urgent,
        data: { type: 'message', message_id: m.id },
      });
      if (result !== 'unavailable') return;
      await desktopNotifications.showNotificationWithAction(
        { title: m.title, body: m.body, tag: messageTag(m.id), requireInteraction: m.urgent },
        () => {
          try {
            window.focus();
          } catch {
            /* ignore */
          }
          navigateRef.current(m.link);
        },
      );
    };

    const fetchOnce = async () => {
      if (!getMemberToken()) return;
      try {
        const out = await fetchMemberNewMessages(cursor);
        if (cancelled) return;
        const first = cursor === null;
        cursor = Math.max(cursor ?? 0, Number(out.cursor) || 0);
        failures = 0;
        if (first) return;
        for (const m of [...(out.messages || [])].reverse()) {
          if (cancelled) return;
          try {
            await show(m);
          } catch {
            /* one notification must not stop the rest */
          }
        }
      } catch {
        failures += 1;
      }
    };

    const poll = (): Promise<void> => {
      if (!inFlight) {
        inFlight = fetchOnce().finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    };

    const schedule = () => {
      if (cancelled) return;
      cancelTimer?.();
      const delay = failures === 0 ? MEMBER_POLL_MS : Math.min(MEMBER_POLL_MS * 2 ** failures, MEMBER_MAX_BACKOFF_MS);
      cancelTimer = backgroundTimeout(async () => {
        cancelTimer = null;
        if (cancelled) return;
        await poll();
        schedule();
      }, delay);
    };

    const catchUp = () => {
      if (cancelled) return;
      void poll().then(schedule);
    };

    void poll().then(schedule);
    window.addEventListener('focus', catchUp);
    window.addEventListener('online', catchUp);
    document.addEventListener('visibilitychange', catchUp);
    return () => {
      cancelled = true;
      cancelTimer?.();
      window.removeEventListener('focus', catchUp);
      window.removeEventListener('online', catchUp);
      document.removeEventListener('visibilitychange', catchUp);
    };
  }, [token]);

  return null;
}

export default MemberMessageNotifier;
